import { useFormContext } from "react-hook-form";
import { includes, map, pull } from "lodash";
import {
  SurveyObject,
  SurveyOption,
} from "../../../../../@types/activities.ts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faSquareCheck } from "@fortawesome/pro-solid-svg-icons";
import { faCircle, faSquare } from "@fortawesome/pro-regular-svg-icons";
import { CSSProperties, useState } from "react";
import classNames from "classnames";
import SurveyOptionVoters from "./SurveyOptionVoters.tsx";

type SurveyOptionsArgs = {
  options: SurveyObject["options"];
  onChange: (optionIds: string[]) => void;
  activityId?: string;
  multiple?: boolean;
  showUniqueVoterCount?: boolean;
  isSchedule?: boolean;
  anonymous?: boolean;
  closed?: boolean;
};

export default function SurveyOptions({
  options,
  onChange,
  multiple,
  showUniqueVoterCount,
  isSchedule = false,
  activityId,
  anonymous,
  closed,
}: SurveyOptionsArgs) {
  const { watch, setValue } = useFormContext();
  const selectedOptionIds = watch("option_ids", []);

  function selectOption(id: string) {
    let newSelectedOptionIds = [...selectedOptionIds];

    if (includes(selectedOptionIds, id)) {
      pull(newSelectedOptionIds, id);
    } else if (multiple) {
      newSelectedOptionIds.push(id);
    } else {
      newSelectedOptionIds = [id];
      isSchedule
        ? setValue("custom_proposal", { date: "", time: "", time_end: "" })
        : setValue("custom_option", "");
    }

    onChange(newSelectedOptionIds);
  }

  return (
    <>
      {map(options, (option, index) => (
        <Option
          key={index}
          {...option}
          showUniqueVoterCount={showUniqueVoterCount}
          selectOption={selectOption}
          multiple={multiple}
          votedFor={includes(selectedOptionIds, option.id)}
          activityId={activityId}
          anonymous={anonymous}
          closed={closed}
        />
      ))}
    </>
  );
}

function Option({
  id,
  label,
  percent,
  alt_percent,
  vote_count,
  showUniqueVoterCount,
  selectOption,
  multiple,
  votedFor,
  activityId,
  anonymous,
  closed,
}: SurveyOption & {
  multiple?: boolean;
  showUniqueVoterCount?: boolean;
  votedFor: boolean;
  selectOption: (id: string) => void;
  activityId?: string;
  anonymous?: boolean;
  closed?: boolean;
}) {
  const [showVoters, setShowVoters] = useState(false);
  return (
    <div>
      <div
        className="option-container flex py-2 px-3 rounded items-center justify-between bg-[linear-gradient(to_right,var(--percent-color)_0%,var(--percent-color)_var(--p),theme(colors.gray.50)_var(--p),theme(colors.gray.50)_100%)]"
        style={
          {
            "--p": `${showUniqueVoterCount ? alt_percent : percent}%`,
            "--percent-color": votedFor
              ? "var(--vote-bar-background-voted)"
              : "var(--vote-bar-background-normal)",
          } as CSSProperties
        }
      >
        <div className="survey-option flex gap-2 items-center">
          <SelectOptionButton
            onSelect={() => selectOption(id)}
            votedFor={votedFor}
            multiple={!!multiple}
            optionId={id}
            optionLabel={label}
            disabled={closed}
          />
          {label}
        </div>
        <div className="option-info flex gap-1 items-center text-sm">
          {!anonymous ? (
            <button
              className={classNames("btn p-0 border-0", {
                hidden: vote_count === 0,
              })}
              type="button"
              onClick={(e) => {
                e.preventDefault();
                setShowVoters(!showVoters);
              }}
            >
              <span className="text-primary hover:underline mr-1">
                ({vote_count})
              </span>
              <span>{showUniqueVoterCount ? alt_percent : percent}%</span>
            </button>
          ) : (
            <span>{showUniqueVoterCount ? alt_percent : percent}%</span>
          )}
        </div>
      </div>
      {showVoters && (
        <SurveyOptionVoters
          activityId={activityId}
          optionId={id}
          show={showVoters}
        />
      )}
    </div>
  );
}

function SelectOptionButton({
  votedFor,
  onSelect,
  multiple,
  optionId,
  optionLabel,
  disabled,
}: {
  votedFor: boolean;
  onSelect: () => void;
  multiple: boolean;
  optionId: string;
  optionLabel: string;
  disabled?: boolean;
}) {
  return (
    <label className="relative cursor-pointer">
      <input
        type={multiple ? "checkbox" : "radio"}
        className="sr-only"
        checked={votedFor}
        onChange={onSelect}
        aria-label={optionLabel}
        name="survey-option"
        value={optionId}
        disabled={disabled}
      />
      <FontAwesomeIcon
        className={classNames("pointer-events-none", {
          "text-muted": disabled,
        })}
        size={window.isApp ? "xl" : undefined}
        icon={
          multiple
            ? votedFor
              ? faSquareCheck
              : faSquare
            : votedFor
              ? faCircleCheck
              : faCircle
        }
      />
    </label>
  );
}
